import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  Mail,
  ArrowRight,
  FileText,
  Wrench,
  Truck,
  Package,
  ShieldCheck,
  RotateCcw,
  CreditCard,
  HelpCircle,
  CheckCircle2,
  PhoneCall,
  ClipboardList,
  CalendarCheck,
  HardHat,
  ChevronDown,
  Clock,
  CalendarDays,
  MapPin,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import adviesOverleg2Img from "@/assets/advies-overleg-2.png.asset.json";

const SITE_URL = "https://elvion-group.nl";

async function trackButtonClick(buttonId: string) {
  try {
    await supabase.from("button_clicks").insert({
      button_id: buttonId,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    });
  } catch (err) {
    console.error("Failed to track click", err);
  }
}

const FAQS = [
  {
    q: "Hoe snel kan ik een offerte verwachten?",
    a: "Na uw aanvraag nemen wij binnen 1 werkdag contact met u op. Voor reguliere offertes ontvangt u binnen 3 werkdagen een compleet voorstel.",
  },
  {
    q: "Wat zijn jullie openingstijden?",
    a: "Onze servicedesk is bereikbaar van maandag tot vrijdag van 08:30 tot 17:00 uur. Voor zakelijke klanten met een onderhoudscontract is er een 24/7 storingsdienst beschikbaar.",
  },
  {
    q: "Hoe meld ik een storing?",
    a: "Bel ons direct op 085 080 66 95. Voor zakelijke klanten met een onderhoudscontract kunt u ook via het klantportaal een storingsmelding indienen. Wij streven ernaar binnen 4 uur te reageren.",
  },
  {
    q: "Hoe retourneer ik een product?",
    a: "U heeft 14 dagen bedenktijd. Meld uw retour aan via info@elvion-group.nl met uw ordernummer. Lees de volledige werkwijze op onze pagina retourbeleid.",
  },
  {
    q: "Welke garantie krijg ik op mijn aankoop?",
    a: "U krijgt altijd de wettelijke garantie volgens het Burgerlijk Wetboek. Veel A-merken (Hikvision, Dahua, AEG) hebben daarnaast 2 tot 5 jaar fabrieksgarantie.",
  },
  {
    q: "Waar kan ik mijn facturen en bestellingen inzien?",
    a: "Al uw facturen, offertes en installatiedocumentatie zijn beschikbaar in het klantportaal. Log in met uw e-mailadres en wachtwoord.",
  },
];

export const Route = createFileRoute("/klantenservice")({
  head: () => {
    const url = `${SITE_URL}/klantenservice`;
    const title = "Klantenservice | Elvion Group Nederland helpt u graag";
    const description =
      "Vraag een offerte aan, meld een storing of stel uw vraag. Elvion Group Nederland helpt u snel verder | telefonisch, per mail of via het klantportaal.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:site_name", content: "Elvion Group Nederland" },
        { property: "twitter:card", content: "summary_large_image" },
        { property: "twitter:title", content: title },
        { property: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "FAQPage",
                mainEntity: FAQS.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "Klantenservice", item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: KlantenservicePage,
});

const topics = [
  {
    icon: FileText,
    title: "Offerte opvragen",
    body: "Vraag vrijblijvend een offerte aan voor elektrotechniek, beveiliging, netwerk of verlichting. Binnen 1 werkdag reactie.",
  },
  {
    icon: Wrench,
    title: "Storing melden",
    body: "Snel hulp bij stroomuitval, een doorgeslagen aardlek of een defect aan uw camerasysteem. Bel 085 080 66 95.",
  },
  {
    icon: Package,
    title: "Bestelstatus",
    body: "Vraag eenvoudig de status van uw bestelling op via het klantportaal of bel onze servicedesk met uw ordernummer.",
  },
  {
    icon: RotateCcw,
    title: "Retourneren",
    body: "14 dagen bedenktijd op alle producten. Wij begeleiden uw retour van aanmelding tot terugbetaling binnen 14 dagen.",
  },
  {
    icon: ShieldCheck,
    title: "Garantie",
    body: "Wettelijke garantie volgens BW én aanvullende fabrieksgarantie tot 5 jaar op veel A-merken zoals AEG en Hikvision.",
  },
  {
    icon: Truck,
    title: "Verhuizing doorgeven",
    body: "Gaat u verhuizen? Geef het tijdig door zodat wij uw installatie of onderhoudscontract netjes overzetten.",
  },
  {
    icon: CreditCard,
    title: "Facturen & betaling",
    body: "Vragen over een factuur, betaalstatus of betaalmethoden? Onze administratie helpt u snel en duidelijk verder.",
  },
  {
    icon: HelpCircle,
    title: "Productadvies",
    body: "Niet zeker welk product u nodig heeft? Onze specialisten geven gratis advies, ook na de aankoop.",
  },
];

const channels = [
  {
    icon: Phone,
    title: "Telefonisch",
    desc: "Direct contact met onze servicedesk",
    actionLabel: "085 080 66 95",
    actionHref: "tel:+31850806695",
  },
  {
    icon: Mail,
    title: "E-mail",
    desc: "Reactie doorgaans binnen 4 uur",
    actionLabel: "info@elvion-group.nl",
    actionHref: "mailto:info@elvion-group.nl",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "1. Stel uw vraag",
    body: "Bel, mail of gebruik het contactformulier. Omschrijf kort uw vraag of probleem.",
  },
  {
    icon: ClipboardList,
    title: "2. Persoonlijke check",
    body: "Een medewerker van de servicedesk pakt uw vraag op en kijkt direct naar uw situatie.",
  },
  {
    icon: CalendarCheck,
    title: "3. Oplossing op maat",
    body: "U ontvangt een duidelijk antwoord, offerte, retourlabel of inplanning | wat van toepassing is.",
  },
  {
    icon: HardHat,
    title: "4. Afhandeling",
    body: "Wij volgen actief op tot uw vraag is opgelost. Achteraf horen wij graag uw ervaring.",
  },
];

const promises = [
  "Reactie binnen 1 werkdag",
  "Vaste, vakkundige medewerkers",
  "Heldere communicatie",
  "14 dagen bedenktijd",
  "Tot 5 jaar fabrieksgarantie",
  "Landelijk werkgebied",
];

const FALLBACK_OPENING_HOURS = [
  { day_order: 1, label: "Maandag - Vrijdag", hours: "08:30 - 17:00", is_closed: false },
  { day_order: 2, label: "Zaterdag", hours: "Op afspraak", is_closed: false },
  { day_order: 3, label: "Zondag & feestdagen", hours: "Gesloten", is_closed: true },
];

const WEEKDAY_NAMES = [
  "zondag",
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
];
const WEEKDAY_INDEX: Record<string, number> = {
  zondag: 7,
  maandag: 1,
  dinsdag: 2,
  woensdag: 3,
  donderdag: 4,
  vrijdag: 5,
  zaterdag: 6,
};

function labelMatchesToday(label: string, todayName: string, todayIdx: number): boolean {
  const lower = label.toLowerCase();
  const rangeMatch = lower.match(
    /(zondag|maandag|dinsdag|woensdag|donderdag|vrijdag|zaterdag)\s*(?:-|–|t\/m|tot)\s*(zondag|maandag|dinsdag|woensdag|donderdag|vrijdag|zaterdag)/,
  );
  if (rangeMatch) {
    const start = WEEKDAY_INDEX[rangeMatch[1]];
    const end = WEEKDAY_INDEX[rangeMatch[2]];
    if (start <= end) return todayIdx >= start && todayIdx <= end;
    return todayIdx >= start || todayIdx <= end;
  }
  return lower.includes(todayName);
}

function parseHoursRange(hours: string): { open: number; close: number } | null {
  const m = hours.match(/(\d{1,2}):(\d{2})\s*(?:-|–|tot)\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return {
    open: parseInt(m[1], 10) * 60 + parseInt(m[2], 10),
    close: parseInt(m[3], 10) * 60 + parseInt(m[4], 10),
  };
}

type OpenStatus = { isOpen: boolean; label: string; detail: string };

function computeOpenStatus(
  openingHours: { day_order: number; label: string; hours: string; is_closed: boolean }[],
  exceptions: { exception_date: string; label: string; hours: string; is_closed: boolean }[],
  now: Date,
): OpenStatus {
  const todayISO = now.toISOString().slice(0, 10);
  const todayName = WEEKDAY_NAMES[now.getDay()];
  const todayIdx = now.getDay() === 0 ? 7 : now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();

  const ex = exceptions.find((e) => e.exception_date === todayISO);
  if (ex) {
    if (ex.is_closed) return { isOpen: false, label: "Vandaag gesloten", detail: ex.label };
    const range = parseHoursRange(ex.hours);
    if (range && minutes >= range.open && minutes < range.close)
      return { isOpen: true, label: "Nu geopend", detail: `${ex.label} · ${ex.hours}` };
    return { isOpen: false, label: "Nu gesloten", detail: `${ex.label} · ${ex.hours}` };
  }

  const match = openingHours.find((o) => labelMatchesToday(o.label, todayName, todayIdx));
  if (!match) return { isOpen: false, label: "Vandaag gesloten", detail: "Geen openingstijden" };
  if (match.is_closed) return { isOpen: false, label: "Vandaag gesloten", detail: match.label };

  const range = parseHoursRange(match.hours);
  if (range) {
    const parts = match.hours.split(/\s*(?:-|–|tot)\s*/);
    if (minutes < range.open)
      return { isOpen: false, label: "Nu gesloten", detail: `Vandaag open vanaf ${parts[0]}` };
    if (minutes >= range.close)
      return { isOpen: false, label: "Nu gesloten", detail: "Vandaag al gesloten" };
    return { isOpen: true, label: "Nu geopend", detail: `Vandaag tot ${parts[1]}` };
  }
  return { isOpen: false, label: "Op afspraak", detail: "Vandaag alleen op afspraak" };
}

function KlantenservicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const { data: openingHours } = useQuery({
    queryKey: ["public-opening-hours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours")
        .select("day_order, label, hours, is_closed")
        .order("day_order", { ascending: true });
      if (error) throw error;
      return data && data.length > 0 ? data : FALLBACK_OPENING_HOURS;
    },
    initialData: FALLBACK_OPENING_HOURS,
  });

  const today = new Date().toISOString().slice(0, 10);
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + 60);
  const horizonISO = horizon.toISOString().slice(0, 10);

  const { data: exceptions } = useQuery({
    queryKey: ["public-opening-hours-exceptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("opening_hours_exceptions")
        .select("id, exception_date, label, hours, is_closed, note")
        .gte("exception_date", today)
        .lte("exception_date", horizonISO)
        .order("exception_date", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    initialData: [],
  });

  return (
    <>
      <Header variant="shop" />
      <main className="bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
          <ContactCta
            imageLeft
            imageUrl={adviesOverleg2Img.url}
            imageAlt="Servicedesk medewerker van Elvion Group Nederland helpt een klant telefonisch"
            title="Liever direct persoonlijk contact?"
            description="Onze klantenservice staat klaar om je vragen over bestellingen, garantie, service en installatie snel en persoonlijk te beantwoorden."
          />
        </div>

        {/* Hero */}
        <section className="bg-card/50">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
            <div className="max-w-2xl">
              <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                Elvion Group Nederland helpt u graag verder
              </h1>
              <p className="mt-4 text-base text-text-secondary sm:text-lg">
                Elvion Group Nederland helpt u graag verder. Een vraag over een
                offerte, een bestelling, een storing of een retour? Onze
                servicedesk staat klaar. Snel, vakkundig en met een vast
                contactpersoon | geen wachtmuziek en geen scripts.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="tel:+31850806695"
                  onClick={() => trackButtonClick("klantenservice_hero_bel")}
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  <Phone className="h-4 w-4" />
                  085 080 66 95
                </a>
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Stuur een bericht
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {channels.map((c) => (
                <div
                  key={c.title}
                  className="flex flex-col justify-between rounded-2xl border border-border bg-background p-5 shadow-sm transition-all hover:border-orange/40 hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                      <c.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-display font-bold text-foreground">{c.title}</p>
                      <p className="text-sm text-text-secondary">{c.desc}</p>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-border pt-4">
                    {c.actionHref ? (
                      <a
                        href={c.actionHref}
                        onClick={() => trackButtonClick(`klantenservice_kanaal_${c.title.toLowerCase()}`)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange hover:underline"
                      >
                        {c.actionLabel}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-foreground/70">
                        {c.actionLabel}
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {/* Opening hours */}
              <div className="flex flex-col justify-between rounded-2xl border border-border bg-background p-5 shadow-sm">
                <div>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-orange">
                      <Clock className="h-5 w-5" />
                      <span className="font-display font-bold text-foreground">Openingstijden</span>
                    </div>
                    {hydrated && (() => {
                      const status = computeOpenStatus(openingHours, exceptions ?? [], new Date());
                      return (
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            status.isOpen
                              ? "bg-green-100 text-green-700"
                              : "bg-muted text-muted-foreground"
                          }`}
                          title={status.detail}
                        >
                          <span
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              status.isOpen ? "bg-green-600" : "bg-muted-foreground"
                            } ${status.isOpen ? "animate-pulse" : ""}`}
                          />
                          {status.label}
                        </span>
                      );
                    })()}
                  </div>
                  {hydrated && (() => {
                    const status = computeOpenStatus(openingHours, exceptions ?? [], new Date());
                    if (!status.detail) return null;
                    return (
                      <p className="mb-3 text-xs text-text-secondary">{status.detail}</p>
                    );
                  })()}
                </div>
                <ul className="space-y-1.5 text-sm">
                  {openingHours.map((o) => (
                    <li key={o.day_order} className="flex items-center justify-between">
                      <span className="text-text-secondary">{o.label}</span>
                      <span
                        className={`font-semibold ${
                          o.is_closed ? "text-muted-foreground" : "text-foreground"
                        }`}
                      >
                        {o.hours}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Address */}
              <div className="flex flex-col justify-between rounded-2xl border border-border bg-background p-5 shadow-sm">
                <div>
                  <div className="mb-3 flex items-center gap-2 text-orange">
                    <MapPin className="h-5 w-5" />
                    <span className="font-display font-bold text-foreground">Bezoek- & postadres</span>
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-foreground">Elvion Group Nederland</p>
                    <p className="text-text-secondary">
                      Betsy van Goorstraat 15<br />
                      6702DC Wageningen<br />
                      Nederland
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-4 text-xs text-text-secondary">
                  <span>KvK 98117475</span>
                  <span>BTW NL868363753B01</span>
                </div>
              </div>

              {/* Why us */}
              <div className="flex flex-col rounded-2xl border border-border bg-background p-5 shadow-sm sm:col-span-2 lg:col-span-1">
                <div className="mb-3 flex items-center gap-2 text-orange">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-display font-bold text-foreground">Waarom onze service?</span>
                </div>
                <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                  {promises.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-text-secondary">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-orange" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {exceptions && exceptions.length > 0 && (
              <div className="mt-4 rounded-2xl border border-orange/30 bg-orange/5 p-4">
                <div className="mb-3 flex items-center gap-2 text-orange">
                  <CalendarDays className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Afwijkende openingstijden
                  </span>
                </div>
                <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {exceptions.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-start justify-between gap-3 rounded-xl border border-border bg-background p-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{e.label}</p>
                        <p className="text-xs text-text-secondary">
                          {new Date(e.exception_date + "T00:00:00").toLocaleDateString("nl-NL", {
                            weekday: "short",
                            day: "numeric",
                            month: "long",
                          })}
                          {e.note ? ` · ${e.note}` : ""}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 text-sm font-semibold ${
                          e.is_closed ? "text-muted-foreground" : "text-foreground"
                        }`}
                      >
                        {e.hours}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Topics grid */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Waarmee kunnen wij u helpen?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Onze servicedesk is breed inzetbaar | van een vraag over een
                bestelling tot een spoedmelding voor een storing of een retour.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {topics.map((t) => (
                <div
                  key={t.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <t.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-foreground">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-sm text-text-secondary">{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Zo werkt onze service
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Een transparant traject | van uw eerste vraag tot een passende
              oplossing.
            </p>
          </div>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step) => (
              <li key={step.title} className="rounded-xl border border-border bg-card p-6">
                <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange">
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-text-secondary">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Detailed sections */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Een offerte opvragen
                </h2>
                <p className="mt-3 text-text-secondary">
                  Vraag vrijblijvend een offerte aan voor uw project. We
                  reageren binnen 1 werkdag, plannen waar nodig een inmeetbezoek
                  en leveren een transparant voorstel met materiaal, arbeid en
                  planning | zonder verrassingen achteraf.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Reactie binnen 1 werkdag</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Vaste prijsafspraken</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inclusief planning en oplevering</li>
                </ul>
                <Link
                  to="/offerte-aanvragen"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
                >
                  Offerte opvragen <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Retourneren binnen 14 dagen
                </h2>
                <p className="mt-3 text-text-secondary">
                  Niet tevreden of toch het verkeerde product besteld? U heeft
                  14 dagen bedenktijd. Meld uw retour aan via e-mail of het
                  klantportaal en u ontvangt binnen 1 werkdag instructies en
                  het retouradres.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Eenvoudige aanmelding</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Terugbetaling binnen 14 dagen</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Volledige uitleg op retourpagina</li>
                </ul>
                <Link
                  to="/retourbeleid"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
                >
                  Naar retourbeleid <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Garantie & service
                </h2>
                <p className="mt-3 text-text-secondary">
                  Op alle producten geldt de wettelijke garantie. Daarnaast
                  hebben veel A-merken zoals Hikvision, Dahua en AEG tot
                  5 jaar aanvullende fabrieksgarantie. Wij regelen de
                  garantieafhandeling van A tot Z voor u.
                </p>
                <Link
                  to="/garantie"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
                >
                  Garantievoorwaarden <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div id="storing" className="scroll-mt-24">

                <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  Storing? Direct contact
                </h2>
                <p className="mt-3 text-text-secondary">
                  Geen stroom, een doorgeslagen aardlek of een defect aan uw
                  camera- of toegangssysteem? Bel ons direct op 085 080 66 95.
                  Zakelijke klanten met een onderhoudscontract hebben toegang
                  tot onze 24/7 storingsdienst.
                </p>
                <a
                  href="tel:+31850806695"
                  onClick={() => trackButtonClick("klantenservice_storing_bel")}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
                >
                  Bel servicedesk <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-8 lg:p-10">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-lg bg-orange/10 p-3 text-orange sm:inline-flex">
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                    Liever zelf regelen via het klantportaal
                  </h2>
                  <p className="mt-3 text-text-secondary">
                    In het klantportaal vindt u uw offertes, facturen,
                    bestellingen en installatiedocumentatie overzichtelijk op
                    één plek. Ook handig om snel een storing te melden of een
                    verhuizing door te geven.
                  </p>
                  <Link
                    to="/auth"
                    onClick={() => trackButtonClick("klantenservice_portaal")}
                    className="mt-5 inline-flex items-center gap-2 rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
                  >
                    Naar het klantportaal <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Veelgestelde vragen
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              De meest gestelde vragen aan onze servicedesk | mogelijk staat uw
              antwoord er al tussen.
            </p>
          </div>
          <div className="mt-8 max-w-3xl space-y-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-semibold text-foreground">{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm leading-relaxed text-muted-foreground">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
